// D2-3 — REFUNDS: THE LAW, SHARED BY THE DASHBOARD AND THE SERVER.
//
// A refund is two separate things happening at once and the whole design turns on keeping them
// separate: MONEY GOES BACK, and ACCESS MAY OR MAY NOT END. Stripe does the first. This site does
// the second, and it is HER CALL PER REFUND rather than a rule — because a goodwill refund on a
// month somebody used is not the same act as undoing a purchase made by mistake.
//
// So every sentence composed here names BOTH: what is being sent back, and what happens to what they
// can open. A confirm step that mentions only the money is how a member ends up refunded and still
// holding the thing she was refunded for, or refunded and locked out with no warning.
//
// Imports nothing, which is the entry requirement: `scripts/d2-refunds.mjs` RUNS it every build.

// ---------------------------------------------------------------------------
// WHAT KIND OF PAYMENT THIS IS. Read off the charge itself, never assumed, because the access
// consequence is completely different for each and the wrong classification is the wrong consequence.
//
//   subscription — the charge carries an INVOICE. Stripe attaches one to every subscription payment
//                  and to nothing else, so this is the reliable signal, not the amount and not the
//                  description.
//   one-time     — it matches a recorded purchase's payment intent. That is our own row, so it is a
//                  fact about this site rather than a guess about Stripe.
//   other        — anything else. It gets NO access consequence offered, because we cannot say what
//                  it bought, and inventing one would be a guess on the access path.
// ---------------------------------------------------------------------------
export const PAYMENT_KINDS = ["subscription", "one-time", "other"];

export function paymentKind(charge = {}, { purchaseIntentIds = [] } = {}) {
  if (charge.invoice) return "subscription";
  const intent = String(charge.paymentIntentId || charge.payment_intent || "");
  if (intent && purchaseIntentIds.map(String).includes(intent)) return "one-time";
  return "other";
}

// ---------------------------------------------------------------------------
// WHAT MAY HAPPEN TO ACCESS, PER KIND. The dashboard offers exactly these and the server refuses
// anything else, so a choice cannot arrive that the confirm sentence has no words for.
// ---------------------------------------------------------------------------
export const ACCESS_ACTIONS = {
  // Her two options for a subscription payment, in her own words.
  "end-now": {
    key: "end-now",
    label: "End their access now",
    kinds: ["subscription"],
    sentence: "Their membership ends immediately.",
  },
  "period-end": {
    key: "period-end",
    label: "Let it run to the end of the period they paid for",
    kinds: ["subscription"],
    sentence: "They keep the membership until the period they paid for ends, then it stops.",
  },
  // A refunded purchase does not stay bought. This is not a choice: giving the money back for
  // Spirit Keeping and leaving Spirit Keeping playing is not a thing this system will do.
  "revoke-purchase": {
    key: "revoke-purchase",
    label: "Take back what the purchase unlocked",
    kinds: ["one-time"],
    sentence: "What that purchase unlocked is taken back.",
  },
  none: {
    key: "none",
    label: "Change nothing about their access",
    kinds: ["subscription", "one-time", "other"],
    sentence: "Nothing about what they can open changes.",
  },
};

export function accessActionsFor(kind) {
  return Object.values(ACCESS_ACTIONS).filter((a) => a.kinds.includes(String(kind)));
}

export const isAccessAction = (kind, action) =>
  accessActionsFor(kind).some((a) => a.key === String(action || ""));

// A refunded one-time purchase defaults to being taken back, and a subscription defaults to the
// gentler of her two options. Defaults are the thing a tired hand accepts, so each one is set to the
// answer that is hardest to regret.
export const defaultAccessAction = (kind) =>
  kind === "one-time" ? "revoke-purchase" : kind === "subscription" ? "period-end" : "none";

// ---------------------------------------------------------------------------
// THE AMOUNT. Everything is in CENTS, integers only, all the way through — a refund computed in
// floating-point dollars is a refund that is occasionally one cent wrong, forever.
// ---------------------------------------------------------------------------
export const refundableCents = (charge = {}) =>
  Math.max(0, Number(charge.amountCents || 0) - Number(charge.amountRefundedCents || 0));

export function money(cents, currency = "usd") {
  const n = Number(cents || 0) / 100;
  const code = String(currency || "usd").toUpperCase();
  const formatted = n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return code === "USD" ? `$${formatted}` : `${formatted} ${code}`;
}

// ---------------------------------------------------------------------------
// R77 — **THE ONE BOX ON THIS SITE WHERE A TYPO SPENDS HER MONEY, AND A NON-NUMBER WAS
// INDISTINGUISHABLE FROM AN EMPTY ONE.**
//
// The refund screen read its amount box as `Math.round(Number(raw) * 100)` and put the result
// straight in the body. `Number("12o")` is NaN, `Math.round(NaN)` is NaN, and **`JSON.stringify`
// writes NaN as `null`** — which is this route's DECLARED way of saying "the whole of what is left"
// (see the comment on the guard below). So a typed amount that could not be read did not refuse and
// did not warn: **it became a FULL REFUND**, and the confirm sentence she then approved described a
// number she had never typed.
//
// **THE HALF THAT MATTERS: THIS CANNOT BE CAUGHT ON THE SERVER.** `Number.isInteger` two functions
// down would refuse a NaN on sight — it is on the far side of a wire that cannot carry one. So the
// readability question is answered on the CLIENT, where the text still exists, and the range
// question stays on the SERVER, where the live charge does. One law, one file, two readers
// (R13/R14/R15: open both sides), and an unreadable amount is REFUSED rather than replaced (R44).
export const REFUND_AMOUNT_UNREADABLE =
  "That amount could not be read. Type it in dollars and cents, like 12.50 — or leave it empty to refund the whole of what is left.";

export function refundAmountFromInput(raw) {
  const text = String(raw === null || raw === undefined ? "" : raw).trim();
  // An empty box means the whole of what is left. That is the common case, it is deliberate, and it
  // is the ONLY thing allowed to travel as `null` — which is the entire point of this function.
  if (text === "") return { kind: "all", cents: null };
  const n = Number(text);
  // `Number.isFinite` and not `!Number.isNaN`: "1e999" is Infinity, and `Math.round(Infinity)` is
  // Infinity, which `JSON.stringify` also writes as `null` (R55: a guard that looks like it is
  // checking the value can be checking a different value's failure mode).
  if (!Number.isFinite(n)) return { kind: "unreadable", cents: null };
  const cents = Math.round(n * 100);
  if (!Number.isFinite(cents)) return { kind: "unreadable", cents: null };
  return { kind: "cents", cents };
}

export function refundRequestViolations({ charge = null, amountCents = null, accessAction = "", kind = "" } = {}) {
  const issues = [];
  if (!charge || !charge.id) { issues.push("That payment could not be found."); return issues; }
  const max = refundableCents(charge);
  if (max <= 0) issues.push("That payment has already been refunded in full.");

  // `null` means "the whole of what is left", which is the common case and is not an error.
  if (amountCents !== null && amountCents !== undefined && amountCents !== "") {
    const n = Number(amountCents);
    if (!Number.isInteger(n)) issues.push("Refund amounts are in whole cents.");
    else if (n <= 0) issues.push("A refund has to be more than nothing.");
    else if (n > max) issues.push(`That is more than is left to refund (${money(max, charge.currency)}).`);
  }
  if (!isAccessAction(kind, accessAction)) issues.push("Choose what happens to their access.");
  return issues;
}

// ---------------------------------------------------------------------------
// D2-4 — THE CONFIRM SENTENCE. It names the money, the person, the payment, AND the access
// consequence, because those are the two things a refund does and she is confirming both.
//
// Composed on the SERVER from the charge Stripe just handed back — never from what the dashboard
// believed the charge was. A confirm that describes a stale amount is a confirm about a different
// payment.
// ---------------------------------------------------------------------------
export function refundConfirmSentence({ charge = {}, amountCents = null, accessAction = "none", email = "" } = {}) {
  const cents = amountCents === null || amountCents === undefined || amountCents === ""
    ? refundableCents(charge)
    : Number(amountCents);
  const full = cents >= refundableCents(charge) && Number(charge.amountRefundedCents || 0) === 0;
  const who = String(email || "").trim().toLowerCase() || "this member";
  const what = charge.description ? ` for ${charge.description}` : "";
  const when = charge.createdAt ? ` from ${new Date(charge.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}` : "";
  const effect = ACCESS_ACTIONS[String(accessAction)]?.sentence || ACCESS_ACTIONS.none.sentence;
  return `Refund ${money(cents, charge.currency)}${full ? " (the full payment)" : " (part of the payment)"} to ${who}${what}${when}. ${effect} Confirm.`;
}

// What the log line says afterwards, built from what ACTUALLY happened rather than from what was
// asked for — the amount Stripe confirmed, the refund id it issued, the consequence we applied.
export function refundLogSummary({ amountCents = 0, currency = "usd", refundId = "", accessAction = "none", applied = true } = {}) {
  const effect = ACCESS_ACTIONS[String(accessAction)]?.sentence || ACCESS_ACTIONS.none.sentence;
  return `${money(amountCents, currency)} refunded${refundId ? ` (${refundId})` : ""}. ${applied ? effect : `${effect} — NOT applied, see detail.`}`;
}
